import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  COMMISSIONING_STEP,
  CommissioningStore,
  PAIRING_STEPS,
  RemoteDeviceType,
  ZIGBEE_COORDINATOR_STATUS,
} from '../../../../../@store';
import { ModalAction } from '../commissioning.model';
import { CommissioningService } from '../commissioning.service';

@Component({
  templateUrl: './commissioning-pairing-modal.component.html',
  styleUrls: ['./commissioning-pairing-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningPairingModalComponent implements OnInit, OnDestroy {
  pairingTitle: string;
  pairingDescription: string;
  selectedDevice: RemoteDeviceType;

  getInstructionForSelectedDevice(): string {
    let properInstruction = '';
    switch (this.selectedDevice) {
      case RemoteDeviceType.TRV:
      case RemoteDeviceType.REPEATER:
        properInstruction = '+SRC_COMMISSIONINGV2.STEPPER_PAIRING_STATUS_INITIATING_DESC_TRV';
        break;
      case RemoteDeviceType.FHT:
        properInstruction = '+SRC_COMMISSIONINGV2.STEPPER_PAIRING_STATUS_INITIATING_DESC_FHT';
        break;
      default:
        properInstruction = '+SRC_COMMISSIONINGV2.STEPPER_PAIRING_STATUS_INITIATING_DESC';
    }

    return this.translateService.instant(properInstruction);
  }
  private readonly statuses = {
    [ZIGBEE_COORDINATOR_STATUS.INITIATING]: {
      titleKey: this.translateService.instant(
        '+SRC_COMMISSIONINGV2.STEPPER_PAIRING_STATUS_INITIATING'
      ),
      descriptionKey: this.translateService.instant(
        '+SRC_COMMISSIONINGV2.STEPPER_PAIRING_STATUS_INITIATING_DESC'
      ),
    },
    [ZIGBEE_COORDINATOR_STATUS.LEARNING]: {
      titleKey: this.translateService.instant(
        '+SRC_COMMISSIONINGV2.STEPPER_PAIRING_STATUS_LEARNING'
      ),
      descriptionKey: this.translateService.instant(
        '+SRC_COMMISSIONINGV2.STEPPER_PAIRING_STATUS_LEARNING_DESC'
      ),
    },
    [ZIGBEE_COORDINATOR_STATUS.CONNECTING]: {
      titleKey: this.translateService.instant(
        '+SRC_COMMISSIONINGV2.STEPPER_PAIRING_STATUS_CONNECTING'
      ),
      descriptionKey: this.translateService.instant(
        '+SRC_COMMISSIONINGV2.STEPPER_PAIRING_STATUS_CONNECTING_DESC'
      ),
    },
  };

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private commissioningService: CommissioningService,
    private commissioningStore: CommissioningStore,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<CommissioningPairingModalComponent>
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('768px');
    this.dialogRef.addPanelClass('commissioning-modal');
    this.commissioningStore.setActiveStep(COMMISSIONING_STEP.PAIRING);
    this.commissioningStore.updateStatus();

    this.commissioningStore.activeStep$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((step) => {
      const moveNextStep =
        step === COMMISSIONING_STEP.PAIRING_SUCCESS ||
        step === COMMISSIONING_STEP.PAIRING_FAILED ||
        step === COMMISSIONING_STEP.PAIRING_ENDED;

      if (moveNextStep) {
        this.commissioningService.modalAction$.next(ModalAction.NextStep);
      }
    });
    this.commissioningStore.deviceType$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((device) => {
      this.selectedDevice = device;
    });

    this.commissioningStore.pairingStatus$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((status) => {
        const pairingTitleKey = this.statuses[status.status].titleKey;
        const pairingDescriptionKey =
          status.status === ZIGBEE_COORDINATOR_STATUS.INITIATING &&
          status.step === PAIRING_STEPS.INITIATING
            ? this.getInstructionForSelectedDevice()
            : this.statuses[status.status].descriptionKey;

        this.pairingTitle = `${this.translateService.instant(pairingTitleKey)} (${status.step})`;
        this.pairingDescription = this.translateService.instant(pairingDescriptionKey);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
